import ICarrierData from '../types/Carriers';
import type { IActiveFlightRange } from '../services/FlightRangeService';

/**
 * Filter out specified carrier codes from a list of carriers
 * 
 * @param carriers - The array of carrier data to filter
 * @param excludeCodes - Array of carrier codes to exclude (default: ['AS', 'QX'])
 * @returns Filtered array of carrier data
 */
export const partnerCarriers = (
  carriers: ICarrierData[], 
  excludeCodes: string[] = ['AS', 'QX']
): ICarrierData[] => {
  // Create a normalized version of exclude codes (trimmed)
  const normalizedExcludeCodes = excludeCodes.map(code => code.trim());
  
  return carriers.filter(carrier => {
    // Normalize the carrier code by trimming whitespace
    const normalizedCarrierCode = carrier.carrierCode.trim();
    
    // Check if the normalized carrier code is in the exclude list
    return !normalizedExcludeCodes.includes(normalizedCarrierCode);
  });
};

/**
 * Get distinct operating carriers from active flight ranges for efficient lookups
 * @param activeFlightRanges - Array of active flight ranges
 * @returns Set of distinct operating carrier codes (trimmed)
 */
export const getDistinctActiveCarriers = (
  activeFlightRanges: IActiveFlightRange[]
): Set<string> => {
  if (!Array.isArray(activeFlightRanges)) {
    return new Set();
  }

  const distinctCarriers = new Set<string>();

  activeFlightRanges.forEach(range => {
    if (range.operatingCarrier) {
      distinctCarriers.add(range.operatingCarrier.trim());
    }
  });

  return distinctCarriers;
};

/**
 * Check if a carrier is present in a set of distinct active carriers
 * @param carrierCode - The carrier code to check
 * @param distinctActiveCarriers - Set of distinct active carrier codes
 * @returns boolean - True if carrier is found in the set
 */
export const isActivePartner = (
  carrierCode: string,
  distinctActiveCarriers: Set<string>
): boolean => {
  if (!carrierCode) {
    return false;
  }

  return distinctActiveCarriers.has(carrierCode.trim());
};
